import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { DEFAULT_GAME_SETTINGS, STORAGE_KEYS } from '@/shared/constants';
// 设置相关atoms
// AI模型配置存储
export const aiModelConfigsAtom = atomWithStorage(STORAGE_KEYS.AI_MODEL_CONFIGS, {});
// 默认游戏设置
export const defaultGameSettingsAtom = atomWithStorage('default_game_settings', DEFAULT_GAME_SETTINGS);
// 当前选中的AI模型配置ID
export const currentAIModelIdAtom = atom('default');
// 当前AI模型配置
export const currentAIModelConfigAtom = atom((get) => {
    const modelId = get(currentAIModelIdAtom);
    const configs = get(aiModelConfigsAtom);
    // 如果有自定义配置，使用自定义配置；否则使用默认配置
    return configs[modelId] || get(defaultGameSettingsAtom).aiModel;
});
// 应用级别设置
export const appSettingsAtom = atomWithStorage('app_settings', {
    // 外观设置
    theme: 'auto',
    language: 'en',
    fontSize: 'medium',
    fontFamily: 'system',
    reducedMotion: false,
    // 行为设置
    autoSave: true,
    autoSaveInterval: 5, // 分钟
    confirmBeforeDelete: true,
    showWelcomeScreen: true,
    // 通知设置
    enableNotifications: true,
    enableSoundEffects: false,
    notificationDuration: 5000, // 毫秒
    // 隐私设置
    telemetryEnabled: false,
    errorReportingEnabled: true,
    // 高级设置
    debugMode: false,
    maxSessionHistory: 1000, // 最大消息历史数量
    maxAutoSaves: 10, // 最大自动存档数量
});
// Removed: keyboard shortcuts settings
// 游戏特定设置（当前游戏的设置覆盖）
export const currentGameSettingsAtom = atom({});
// 合并后的游戏设置（默认 + 当前游戏覆盖）
export const effectiveGameSettingsAtom = atom((get) => {
    const defaultSettings = get(defaultGameSettingsAtom);
    const currentOverrides = get(currentGameSettingsAtom);
    return {
        ...defaultSettings,
        ...currentOverrides,
    };
});
// 导入导出设置
export const importExportSettingsAtom = atom({
    exportFormat: 'json',
    includeMetadata: true,
    includeSavePoints: true,
    compression: false,
});
// 开发者设置（仅在调试模式下可见）
export const developerSettingsAtom = atomWithStorage('developer_settings', {
    debugMode: false,
    showPerformanceMetrics: false,
    logLevel: 'info',
    mockAIResponses: false,
    maxLogEntries: 1000,
});
// 设置操作相关的写入atoms
// 添加或更新AI模型配置
export const saveAIModelConfigAtom = atom(null, (get, set, { id, config }) => {
    const configs = get(aiModelConfigsAtom);
    set(aiModelConfigsAtom, {
        ...configs,
        [id]: config,
    });
});
// 删除AI模型配置
export const deleteAIModelConfigAtom = atom(null, (get, set, configId) => {
    const configs = get(aiModelConfigsAtom);
    const { [configId]: deleted, ...remaining } = configs;
    set(aiModelConfigsAtom, remaining);
    // 如果删除的是当前选中的配置，切换到默认配置
    const currentId = get(currentAIModelIdAtom);
    if (currentId === configId) {
        set(currentAIModelIdAtom, 'default');
    }
});
// 更新默认游戏设置
export const updateDefaultGameSettingsAtom = atom(null, (get, set, updates) => {
    const current = get(defaultGameSettingsAtom);
    set(defaultGameSettingsAtom, {
        ...current,
        ...updates,
    });
});
// 更新应用设置
export const updateAppSettingsAtom = atom(null, (get, set, updates) => {
    const current = get(appSettingsAtom);
    set(appSettingsAtom, {
        ...current,
        ...updates,
    });
});
// 更新键盘快捷键
// Removed: updateKeyboardShortcutsAtom
// 重置所有设置到默认值
export const resetAllSettingsAtom = atom(null, (get, set) => {
    set(defaultGameSettingsAtom, DEFAULT_GAME_SETTINGS);
    set(appSettingsAtom, {
        theme: 'auto',
        language: 'en',
        fontSize: 'medium',
        fontFamily: 'system',
        reducedMotion: false,
        autoSave: true,
        autoSaveInterval: 5,
        confirmBeforeDelete: true,
        showWelcomeScreen: true,
        enableNotifications: true,
        enableSoundEffects: false,
        notificationDuration: 5000,
        telemetryEnabled: false,
        errorReportingEnabled: true,
        debugMode: false,
        maxSessionHistory: 1000,
        maxAutoSaves: 10,
    });
    // keyboard shortcuts removed
});
// 导出所有设置
export const exportAllSettingsAtom = atom((get) => {
    const settings = {
        defaultGameSettings: get(defaultGameSettingsAtom),
        appSettings: get(appSettingsAtom),
        aiModelConfigs: get(aiModelConfigsAtom),
        exportedAt: new Date().toISOString(),
        version: '0.1.0',
    };
    return JSON.stringify(settings, null, 2);
});
// 导入设置
export const importSettingsAtom = atom(null, (get, set, settingsJson) => {
    try {
        const settings = JSON.parse(settingsJson);
        // 验证设置格式并导入
        if (settings.defaultGameSettings) {
            set(defaultGameSettingsAtom, settings.defaultGameSettings);
        }
        if (settings.appSettings) {
            set(appSettingsAtom, settings.appSettings);
        }
        // keyboard shortcuts removed
        if (settings.aiModelConfigs) {
            set(aiModelConfigsAtom, settings.aiModelConfigs);
        }
        return { success: true };
    }
    catch (error) {
        return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
});
// 验证AI模型配置
export const validateAIConfigAtom = atom(null, async (get, set, config) => {
    // 这里可以添加验证逻辑，比如测试API密钥
    // 暂时返回成功
    return { valid: true };
});
// 测试AI模型连接
export const testAIConnectionAtom = atom(null, async (get, set, config) => {
    // 这里可以添加实际的连接测试逻辑
    // 暂时模拟测试
    return new Promise((resolve) => {
        setTimeout(() => {
            resolve({
                success: Math.random() > 0.3, // 70% 成功率模拟
                message: Math.random() > 0.3 ? 'Connection successful' : 'Connection failed: Invalid API key',
            });
        }, 1000);
    });
});
